import { useEffect, useState } from "react";
import { useOutletContext, useNavigate, useParams, Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { ScanLine, ArrowLeft, CalendarDays } from "lucide-react";
import { formatDate } from "@/lib/format";

// Very thin wrapper: presents the venue's upcoming events with a Check-in button that
// routes into the existing /workspace check-in UX (which already supports RBAC).
export default function VenueCheckIn() {
    const { venue } = useOutletContext();
    const { eid } = useParams();
    const nav = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        setLoading(true);
        endpoints.venueEvents(venue.id).then((d) => {
            const upcoming = (d.grouped?.upcoming || []);
            setEvents(upcoming);
        }).finally(() => setLoading(false));
    }, [venue.id]);

    // If eid provided → redirect to existing workspace check-in (which already has full QR/manual UX)
    useEffect(() => {
        if (eid) nav(`/workspace/check-in/${eid}`);
    }, [eid, nav]);

    if (eid) return null;

    return (
        <div className="pm-fade" data-testid="venue-checkin-page">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Check-in</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Choose an event</h1>
            </header>
            <div className="px-4 md:px-8 py-6">
                {loading ? <div className="font-mono text-xs text-[#666]">Loading…</div>
                : events.length === 0 ? (
                    <div className="border border-dashed border-[#2a2a2a] p-8 text-center" data-testid="venue-checkin-empty">
                        <CalendarDays className="w-8 h-8 text-[#666] mx-auto" />
                        <div className="mt-3 text-sm text-[#a3a3a3]">No upcoming events at this venue.</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {events.map((e) => (
                            <Link key={e.id} to={`/workspace/check-in/${e.id}`} className="border border-[#2a2a2a] p-4 hover:border-[#ff4400] flex items-center justify-between" data-testid={`venue-checkin-event-${e.id}`}>
                                <div className="min-w-0">
                                    <div className="font-display font-bold uppercase truncate">{e.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">{formatDate(e.date)} · {e.registrations}/{e.capacity}</div>
                                </div>
                                <ScanLine className="w-5 h-5 text-[#ff4400] shrink-0" />
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
